import { Component, inject, signal, input } from '@angular/core';
import { combineLatest, filter, map, of, take, tap } from 'rxjs';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { CrmInjectionToken, LoadingStatus, ObjectType } from '../../tokens-and-interfaces';
import { CRMTrackingService } from '../../shared-services/page-analytics.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { CrmAISummaryModalComponent, CrmAISummaryModalData } from './ai-summary-modal.component';
import { MatButton } from '@angular/material/button';
import { switchMap, withLatestFrom } from 'rxjs/operators';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'crm-ai-summary-button',
  templateUrl: './ai-summary-button.component.html',
  styleUrls: ['./ai-summary-button.component.scss'],
  standalone: true,
  imports: [TranslateModule, MatButton, MatIcon],
})
export class CrmAISummaryButtonComponent {
  private readonly config = inject(CrmInjectionToken);
  private readonly aiIconService = inject(GalaxyAiIconService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly trackingService = inject(CRMTrackingService);
  private readonly dialog = inject(MatDialog);

  readonly crmObjectId = input.required<string>();
  readonly crmObjectType = input.required<ObjectType>();
  readonly crmObjectName = input<string>('');
  readonly loadingCrmObjectName = input.required<LoadingStatus>();

  private readonly crmObjectId$ = toObservable(this.crmObjectId);
  private readonly crmObjectType$ = toObservable(this.crmObjectType);
  private readonly loadingCrmObjectName$ = toObservable(this.loadingCrmObjectName);

  protected readonly showAISummaryRestrictedDialog = signal<boolean>(false);
  protected readonly hasAccessToAISummary$ = this.config.hasAccessToFeature$?.('crm-ai-summary') ?? of(false);
  protected readonly hasAccessToAISummary = toSignal(this.hasAccessToAISummary$);

  constructor() {
    this.aiIconService.dummyInit();

    this.showAISummaryRestrictedDialog.set(!!this.config.openRestrictedDialog);

    // open AI summary modal at the start if aiSummary query param is true
    combineLatest([this.crmObjectId$, this.crmObjectType$, this.loadingCrmObjectName$])
      .pipe(
        filter(
          ([objectId, objectType, loadingCrmObjectName]) =>
            !!objectId && !!objectType && loadingCrmObjectName !== LoadingStatus.LOADING,
        ),
        switchMap(() => this.activatedRoute.queryParamMap),
        take(1),
        map((params) => params.get('aiSummary') || ''),
        map((aiSummary) => aiSummary === 'true'),
        tap((hasAISummaryParam) => {
          if (hasAISummaryParam) {
            this.trackingService.trackEvent(this.crmObjectType() as ObjectType, 'used-ai-quick-summary-url-parameter');
          }
        }),
        withLatestFrom(this.hasAccessToAISummary$),
      )
      .subscribe(([hasAISummaryParam, hasAccessToAISummary]) => {
        if (hasAISummaryParam && hasAccessToAISummary) {
          this.openAISummaryModal();
        }
      });
  }

  protected openRestrictedModal() {
    this.config.openRestrictedDialog?.('crm-ai-summary');
  }

  protected openAISummaryModal() {
    this.dialog
      .open(CrmAISummaryModalComponent, {
        width: '700px',
        autoFocus: false,
        data: {
          objectId: this.crmObjectId(),
          objectType: this.crmObjectType(),
          objectName: this.crmObjectName(),
        } as CrmAISummaryModalData,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe();
  }
}
